<template>
  <GlPageWrap>
    <transition
      appear
      mode="out-in"
      name="scale-appear"
    >
      <GenerateReportLoader
        v-show="getIsGenerateReportLoaderVisible"
        :is-first-generate-loader="isFirstGenerateLoader"
      />
    </transition>

    <template v-if="!getIsGenerateReportLoaderVisible">
      <div
        v-if="startAddressCheckLoading"
        class="report__loader"
      >
        <gl-loader progress-spinner />
      </div>
      <div
        v-else
        class="report-wrapper"
        :class="{ 'report-wrapper--legacy': getIsLegacy }"
      >
        <div class="report">
          <div class="report-type-title">
            {{ reportTypeTitle }}
          </div>
          <ChooseTokenList
            v-if="coinData.supportContracts && !reportPartsLoading"
            :address-hash="searchValue"
            class="report-token-list"
            :disable="reportPartsLoading"
            :request-type="'address'"
            :selected-token="selectedToken"
            :tokens-list="tokensList"
            @set-token="changeToken"
          />
          <AddressReport
            v-if="isAddressReport"
            ref="addressReport"
            :address-data="addressInfo"
            :address-part="addressPart"
            :all-data-source="allDataSource"
            :all-data-source-by-owner="allDataSourceByOwner"
            :currencies-by-direction="currenciesByDirection"
            :current-score-error-message="currentScoreErrorMessage"
            :full-details-part="fullDetailsPart"
            :owner-part="ownerPart"
            :profile-info2="profileInfo2"
            :pure-sources="pureSources"
            :report-parts-loading="reportPartsLoading"
            :risks-part="risksPart"
            :selected-token="selectedToken"
            :total-funds="addressTotalFunds"
          />
          <div
            v-else
            class="report-empty"
          >
            Report empty
          </div>
        </div>
      </div>
    </template>
    <PlansWrapper
      v-if="isPurchaseCardsVisible"
      @buy-report="onBuyClick"
    />
    <OnboardingTour
      :key="isMobile"
      :loading="reportPartsLoading"
    />
  </GlPageWrap>
</template>

<script>
// Vuex
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
//Components
import GlPageWrap from '@/components/layout/gl-page-wrap'
import GlLoader from '@/components/gl-loader'
import AddressReport from '@/pages/report/components/AddressReport'
import ChooseTokenList from '@/pages/report/components/token-report-2/ChooseTokenList.vue'
import GenerateReportLoader from '@/pages/report/components/loader/GenerateReportLoader'
import OnboardingTour from '@/pages/report/components/onboarding/OnboardingTour'
import PlansWrapper from '@/pages/report/components/purchase/PlansWrapper'
import LimitsReachedModal from '@/pages/report/components/purchase/modals/LimitsReachedModal'
// Utils
import { csvExportReport } from '@/utils/export-data'
import { getObjectByKey, moveElementToTop } from '@/utils/helpers'
import { formatShare, formatter } from '@/utils/sourcesFormatter'
import { findColorByTypeScore } from '@/utils/cytoskape-ui-rules'
import { tokensSorting } from '@/utils/cytoskape-ui-rules'
import { formatDate } from '@/utils/format-date'
import { formatAmount, txsAddPlus, formatterAmountValue2 } from '@/utils/format-amount'
import { toComaSeparate } from '@/utils/formatNumber'
import appConfig from '@/utils/appConfig'
import { sortingObjects, sourcesSortOrderArray } from "@/utils/sorting";
import { separateSources, transformToTokenStructure, formatPurchasedInfoPayload } from '@/utils/report-data-formatter'
import { trancateString } from '@/utils/text-formatter'
import { calculateScoreByRiskLevel } from '@/utils/risk-formatter'
import { openProfilePlan } from '@/utils/go-to-route'
// services
import { syncOnboardingState, startReportOnboardingTours, stopAllTours } from '@/services/onboarding-tour'
//mixins
import confirmModalMixin from '@/assets/mixins/confirmModalMixin'
import csvExport from '@/assets/mixins/csvExport'
import deviceWidthMixin from '@/assets/mixins/deviceWidthMixin'

// model
import { getReportPartsExample, getStartAddressCheckExample } from '@/pages/your-reports/model/report-example'
import axios from 'axios'

export default {
  components: {
    GlPageWrap,
    GlLoader,
    AddressReport,
    ChooseTokenList,
    GenerateReportLoader,
    OnboardingTour,
    PlansWrapper,
  },
  mixins: [confirmModalMixin, csvExport, deviceWidthMixin],
  data() {
    return {
      search: '',
      addressPart: {},
      ownerPart: {},
      scorePart: {
        data: {
          sof: [],
          uof: [],
          totalScore: null
        }
      },
      fullDetailsPart: {},
      tier2Part: {},
      reportGeneratedAt: '',
      addressInfo: {},
      addressData: {},
      isAddressReport: false,
      isEthAddressReport: false,
      startAddressCheckLoading: true,
      reportPartsLoading: false,
      reloadReportLoading: false,
      allDataSource: {},
      allDataSourceByOwner: {},
      allDataSourceByOwnerType: [],
      tokensList: [],
      addressTotalFunds: 0,
      selectedToken: {
        symbol: '',
        address: '',
      },
      searchValue: '',
      profileInfo2: {},
      pureSources: {},
      currenciesByDirection: {},
      currentScoreErrorMessage: {},
      risksPart: {},
      uofPart: {},
      sofPart: {},
      needLoadReport: true,
      isFirstPartsLoaded: false,
      cancelRequestsSource: null,
    }
  },
  computed: {
    ...mapGetters('user', ['isLogin', 'isB2bUser', 'getOnboardingParams']),
    ...mapState('analytics', ['coinType', 'coinData', 'currencyList']),
    ...mapGetters('directions', ['getActiveExposurePart', 'getExposurePartOptions', 'getActivePartBar', 'getPartBarOptions']),
    ...mapGetters('report', ['getSofTransactions', 'getUofTransactions', 'getPurchasedInfo', 'getNoSentTransactions', 'getCurrentPrices', 'getReportHeaderEmit', 'getIsGenerateReportLoaderVisible', 'getReloadInfo', 'getIsLegacy', 'getExampleId']),
    ...mapGetters('payment', ['getCurrentPlan']),
    appConfig() {
      return appConfig
    },
    enableGeneratePdf() {
      return appConfig.VUE_APP_PDF_GENERATOR
    },
    addressReport() {
      return this.isAddressReport || this.isEthAddressReport
    },
    reportTypeTitle() {
      if (this.addressPart.type == 'address') return 'Address Risk Report'
      if (this.addressPart.type == 'contract') return 'Smart Contract Report'
      if (this.addressPart.type == 'tokenContract')
        return 'Token Contract Report'
      return ''
    },
    combinedState() {
      return [this.reportPartsLoading, this.startAddressCheckLoading, this.isFirstGenerateLoader, this.reloadReportLoading, this.addressPart.formattedData]
    },
    isFirstGenerateLoader() {
      return !this.isFirstPartsLoaded
    },
    isPurchaseCardsVisible() {
      return !this.getExampleId && !this.startAddressCheckLoading && !this.getIsGenerateReportLoaderVisible && !(this.getPurchasedInfo.full || this.getPurchasedInfo.tier2 || this.getPurchasedInfo.risks) && !this.getIsLegacy && !this.getExampleId
    },
  },
  watch: {
    combinedState: {
      handler() {
        this.SET_REPORT_HEADER_DATA({
          formattedData: this.addressPart?.formattedData,
          startAddressCheckLoading: this.startAddressCheckLoading,
          reportPartsLoading: this.reportPartsLoading,
          reloadReportLoading: this.reloadReportLoading,
        })
      },
      deep: true
    },
    getReportHeaderEmit: {
      handler(newValue) {
        if (newValue.eventType === 'reload') {
          this.onReloadReport()
        }
      }
    },
    getIsGenerateReportLoaderVisible: {
      handler(newValue) {
        if (newValue) return
        setTimeout(() => {
          this.startReportIntro()
        }, 1500)
      },
    },
    isDesktop: {
      handler(val) {
        this.setFieldInExposurePart('full', 'hide', Boolean(!val))
        this.setFieldInExposurePart('full', 'disabled', Boolean(!val))
        this.changeToAvailableExposurePart()
      },
      immediate: true
    },
  },
  beforeDestroy() {
    const container = document.getElementById('main-container')
    container?.removeEventListener('scroll', this._onContainerScroll)
    document.documentElement.classList.remove('buy-block--sticked')
    document.documentElement.classList.remove('header-hidden')
    this.stopAllTours(this)
    if (this.cancelRequestsSource) {
      this.cancelRequestsSource.cancel('custom_request_cancel')
    }
    this.SET_IS_LEGACY(null)
  },
  async mounted() {
    this.watcherForScroll()
    if (this.isLogin) {
      await this.getMe()
      await this.syncOnboardingState({ remote: this.getOnboardingParams })
    }
    this.initQuery()
  },
  methods: {
    ...mapMutations('analytics', ['SET_COIN_TYPE', 'SET_COIN_DATA']),
    ...mapActions('modal', ['openModal']),
    ...mapMutations({
      SET_REPORT_INFO: 'report/SET_REPORT_INFO',
      SET_ACTIVE_TOKEN: 'report/SET_ACTIVE_TOKEN',
      SET_PURCHASED_INFO: 'report/SET_PURCHASED_INFO',
      SET_IS_LEGACY: 'report/SET_IS_LEGACY',
      SET_EXAMPLE_ID: 'report/SET_EXAMPLE_ID',
      SET_RELOAD_INFO: 'report/SET_RELOAD_INFO',
      SET_REPORT_HEADER_DATA: 'report/SET_REPORT_HEADER_DATA',
    }),
    ...mapActions('report', [
      'getStartAddressCheck',
      'getReportParts',
      'getEtherscanWalletRisk',
      'purchaseReports',
      'reloadReport',
    ]),
    ...mapActions('user', ['getMe']),
    ...mapActions('payment', ['fetchCurrentPlan']),
    ...mapMutations('directions', ['SET_ACTIVE_EXPOSURE_PART', 'SET_EXPOSURE_PART_OPTIONS', 'SET_PART_BAR_OPTIONS', 'SET_ACTIVE_PART_BAR']),
    formatter,
    formatShare,
    tokensSorting,
    csvExportReport,
    findColorByTypeScore,
    getObjectByKey,
    formatDate,
    moveElementToTop,
    formatAmount,
    txsAddPlus,
    toComaSeparate,
    formatterAmountValue2,
    sortingObjects,
    separateSources,
    transformToTokenStructure,
    trancateString,
    startReportOnboardingTours,
    syncOnboardingState,
    stopAllTours,
    calculateScoreByRiskLevel,
    openProfilePlan,
    formatPurchasedInfoPayload,
    getStartAddressCheckExample,
    initQuery() {
      const { query } = this.$route

      this.initCoinType(query.type)
      this.initSelectedToken(query.token)
      this.SET_EXAMPLE_ID(query.exampleId)
      this.SET_IS_LEGACY(query.isLegacy ? true : null)
      this.initSearchData({ address: query.address, exampleId: query.exampleId, legacy: query.isLegacy })
    },
    initCoinType(type) {
      if (!type) return
      this.SET_COIN_TYPE(type)
      const coin = this.currencyList.find(curr => curr.key === type)
      this.SET_COIN_DATA(coin)
      this.selectedToken.symbol = this.coinData.symbol
    },
    initSelectedToken(token) {
      this.selectedToken.symbol = this.coinData.symbol
      this.selectedToken.address = token || ''

      if (this.coinData.supportContracts) {
        this.selectedToken.address = token ?? 'all'
        if (this.selectedToken.address == 'all') {
          this.selectedToken.symbol = "All assets"
        }
      }
    },
    initSearchData({ address, exampleId, legacy } = {}) {
      if (!address) return
      this.search = address
      if (exampleId || legacy) {
        this.searchData(address, exampleId, legacy)
        return
      }
      this.searchData(address)
    },
    async searchData(value, exampleId, legacy) {
      if (this.cancelRequestsSource) {
        this.cancelRequestsSource.cancel('custom_request_cancel')
      }
      this.cancelRequestsSource = axios.CancelToken.source()

      this.SET_PURCHASED_INFO({})
      this.searchValue = value
      this.clearData()
      this.stopAllTours(this)
      await this.SET_COIN_TYPE(this.coinType)

      if (
        this.$can('use', this.coinData.family) &&
        new RegExp(this.coinData.addressRegex).test(value)
      ) {
        this.isAddressReport = true
        this.startAddressCheckLoading = true

        await this.getStartAddressCheck({
          address: value,
          coin: this.coinData?.key,
          ...(this.coinData.supportContracts ? { token: this.selectedToken.address } : {}),
          exampleId,
          legacy,
          configParams: { cancelToken: this.cancelRequestsSource.token }
        })
          .then(({ data }) => {
            this.takePurchasedInfo(data)
            this.takeReloadInfo(data)
            this.needLoadReport = true

            this.risksPart = {
              ...this.getObjectByKey(data, 'risks', 'reportPartName'),
            }
            this.sofPart = {
              ...this.getObjectByKey(data, 'sof', 'reportPartName'),
            }
            this.uofPart = {
              ...this.getObjectByKey(data, 'uof', 'reportPartName'),
            }
            this.fullDetailsPart = {
              ...this.getObjectByKey(data, 'full', 'reportPartName'),
            }
            this.tier2Part = {
              ...this.getObjectByKey(data, 'tier2', 'reportPartName'),
            }

          })
          .catch(() => {
            const fullExample = this.getStartAddressCheckExample({ exampleId: 'full' })
            const formatPurchasedInfoPayloadResult = formatPurchasedInfoPayload({ purchasedInfo: fullExample.data, currentPlan: this.getCurrentPlan })
            this.takePurchasedInfo(formatPurchasedInfoPayloadResult)
            this.startAddressCheckLoading = false
            this.needLoadReport = false
          })

        const params = {
          address: value,
          coin: this.coinData?.key,
          ...(this.coinData.supportContracts ? { token: this.selectedToken.address } : {}),
          exampleId,
          legacy,
          configParams: { cancelToken: this.cancelRequestsSource.token }
        }

        if (this.needLoadReport) {
          this.startAddressCheckLoading = false
          this.reportPartsLoading = true

          await this.getReportParts(params)
            .then(({ data }) => {
              const { parts, createdAt } = data

              const previewExample = getReportPartsExample({ exampleId: 'preview' })

              this.addressPart = {
                ...this.addressPart,
                ...this.getObjectByKey(parts, 'address', 'type'),
              }
              this.risksPart = {
                ...this.risksPart,
                ...this.getObjectByKey(this.risksPart.purchased ? parts : previewExample.data.parts, 'risks', 'type'),
              }
              this.sofPart = {
                ...this.sofPart,
                ...this.getObjectByKey(this.sofPart.purchased ? parts : previewExample.data.parts, 'sof', 'type'),
              }
              this.uofPart = {
                ...this.uofPart,
                ...this.getObjectByKey(this.uofPart.purchased ? parts : previewExample.data.parts, 'uof', 'type'),
              }
              this.fullDetailsPart = {
                ...this.fullDetailsPart,
                ...this.getObjectByKey(this.fullDetailsPart.purchased ? parts : previewExample.data.parts, 'full', 'type'),
              }
              this.tier2Part = {
                ...this.tier2Part,
                ...this.getObjectByKey(this.tier2Part.purchased ? parts : previewExample.data.parts, 'tier2', 'type'),
              }

              if (!this.risksPart.purchased && this.$route.query?.ref === 'etherscan' && this.coinData?.key === 'eth') {
                this.SET_PURCHASED_INFO({...this.getPurchasedInfo, risksPreview: true})
              }
              this.ownerPart.data = this.risksPart?.data?.owner || {}

              this.setTokens(this.addressPart?.data?.tokens)

              this.profileInfo2 = {
                ...this.profileInfo2,
                clusterData: { ...this.ownerPart.data.clusterData },
                ownerData: {
                  owner: this.ownerPart?.data?.owner,
                  description: this.ownerPart?.data?.description,
                  type: this.ownerPart?.data?.type,
                  tags: this.ownerPart?.data?.tags,
                  meta: this.ownerPart?.data?.meta,
                },
                entityData: { ...this.ownerPart?.data?.entityData },
              }

              const reportInfo = {
                createdAt,
                walletAddress: this.searchValue,
                owner: this.ownerPart?.data?.owner,
                ownerType: this.ownerPart?.data?.type,
                ownerTags: this.ownerPart?.data?.tags,
                balance: this.addressPart?.data?.balance,
                totalTxReceived: this.addressPart?.data?.totalTxReceived,
                totalTxSent: this.addressPart?.data?.totalTxSent,
                numberOfTxs: this.addressPart?.data?.numberOfTxs,
                totalReceived: this.addressPart?.data?.totalReceived,
                totalReceivedPrice: this.addressPart?.data?.totalReceivedPrice,
                totalSent: this.addressPart?.data?.totalSent,
                totalSentPrice: this.addressPart?.data?.totalSentPrice,
                firstSeen: this.addressPart?.data?.firstSeen,
                lastSeen: this.addressPart?.data?.lastSeen,
                riskScore: this.scorePart?.data?.totalScore,
                tags: this.addressPart?.data?.owner?.type?.name
              }

              const tierCurrencies =  Object.values(this.tier2Part?.data?.currencies || {})
              const currenciesTier = transformToTokenStructure([...this.tokensList, ...tierCurrencies ])

              this.setBarLabels({ sources: this.tier2Part.data?.labelBarsSof || [], currencies: currenciesTier }, 'incoming')
              this.setBarLabels({ sources: this.tier2Part.data?.labelBarsUof || [], currencies: currenciesTier }, 'outgoing')

              this.$set(this.allDataSource, 'evaluatedSofTxs', this.tier2Part.data?.evaluatedSofTxs)
              this.$set(this.allDataSource, 'evaluatedUofTxs', this.tier2Part.data?.evaluatedUofTxs)

              this.setFieldInExposurePart('incoming', 'disabled', Boolean(!this.tier2Part.data?.labelBarsSof?.length))
              this.setFieldInExposurePart('outgoing', 'disabled', Boolean(!this.tier2Part.data?.labelBarsUof?.length))
              this.changeToAvailableExposurePart()

              this.setBarEntity({ sources: this.fullDetailsPart.data?.entityBarsSof || [], currencies: currenciesTier }, 'incoming')
              this.setBarEntity({ sources: this.fullDetailsPart.data?.entityBarsUof || [], currencies: currenciesTier }, 'outgoing')

              const sourceCurrenciesSof =  Object.values(this.sofPart?.data?.currencies || {})
              const sourceCurrenciesUof =  Object.values(this.uofPart?.data?.currencies || {})
              const currenciesSof = transformToTokenStructure([...this.tokensList, ...sourceCurrenciesSof ])
              const currenciesUof = transformToTokenStructure([...this.tokensList, ...sourceCurrenciesUof ])

              this.setTxSources({ sources: this.sofPart?.data?.sources || [], currencies: currenciesSof }, 'incoming')
              this.setTxSources({ sources: this.uofPart?.data?.sources || [], currencies: currenciesUof }, 'outgoing')

              this.SET_REPORT_INFO(reportInfo)
              this.reportGeneratedAt = createdAt
              this.formatAddressPart(this.addressPart)
              this.formatOwnerPart(this.addressPart?.data?.owner)
            })
            .finally(async () => {
              this.reportPartsLoading = false
              this.isFirstPartsLoaded = true

              await this.$nextTick()

              if (this.isPrerender) {
                setTimeout(() => {
                  console.log('[Prerender] Report is ready. Dispatching event...');
                  document.dispatchEvent(new Event('x-render-ready'));
                }, 500);
              }

              const anchor = this.$route.query.anchor
              this.scrollToSection(anchor, 500)
              setTimeout(() => {
                this.startReportIntro()
              }, 1500)
            })

          if (this.getPurchasedInfo.risksPreview && this.coinData?.key === 'eth') {
            const walletRiskData = await this.getEtherscanWalletRisk(params)
            this.$set(this.risksPart.data, 'totalScore', this.calculateScoreByRiskLevel(walletRiskData?.risk_band ?? null))
          }

          if (this.$route.name === 'report') {
            const { params, query } = this.$route

            const replaceQuery = {
              address: value,
              type: this.coinType,
              ...(this.coinData.supportContracts ? { token: this.selectedToken.address } : {}),
              ...(exampleId ? { exampleId } : {}),
              ...(legacy ? { isLegacy: legacy } : {}),
              ...(query.ref ? { ref: query.ref } : {}),
            }

            await this.$router
              .replace({
                name: 'report',
                query: replaceQuery,
                params,
              })
              .catch(err => err)
          }
        } else {
          if (!this.selectedToken.address && this.tokensList.length > 0 && this.tokensList.find(contract => contract.address)) {
            this.selectedToken = this.tokensList.find(contract => contract.address)
            return await this.searchData(this.searchValue, exampleId, legacy)
          }

          this.isFirstPartsLoaded = true
          this.addressPart = {}
          this.$set(this.addressPart.formattedData || (this.addressPart.formattedData = {}), 'coinData', this.coinData)
          this.$set(this.addressPart.formattedData, 'walletAddress', this.searchValue)

          this.$toast.add({
            severity: 'error',
            summary: 'Report loading error',
            detail: 'There was a problem loading the report.',
            life: 6000,
          })
        }
        if (this.isLogin) {
          await this.fetchCurrentPlan()
        }

        return
      }
      this.$toast.add({
        severity: 'error',
        summary: 'Error Message',
        detail: 'Search value is not valid',
        life: 3000,
      })
      this.$router.replace({ name: 'source-of-funds' }).catch(() => {})
    },
    clearData() {
      this.addressPart = {}
      this.ownerPart = {}
      this.scorePart = {}
      this.fullDetailsPart = {}
      this.tier2Part = {}
      this.currentPartDataForConfirm = {}
      this.reportGeneratedAt = ''
      this.isAddressReport = false
      this.addressData = null
      this.addressInfo = {}
      this.swapSources = []
      this.allDataSource = {}
      this.allDataSourceByOwner = {}
      this.allDataSourceByOwnerType = []
      this.addressTotalFunds = 0
    },
    setTokens(tokens) {
      this.tokensList = Object.values(tokens ?? {})
      if (!this.tokensList.length) return
      this.tokensList = this.tokensSorting(this.tokensList)
      if (this.tokensList.length > 1) {
        this.tokensList.unshift({address: 'all', symbol: 'All assets'})
      }

      const selectedTokenIndex = this.tokensList.findIndex(el => el.address === this.selectedToken.address)
      this.moveElementToTop(this.tokensList, selectedTokenIndex)
      const allTokenIndex = this.tokensList.findIndex(el => el.address === 'all')
      this.moveElementToTop(this.tokensList, allTokenIndex)
      this.selectedToken = this.tokensList.find(e => e.address == this.selectedToken.address) || this.tokensList.find(e => e.address == this.coinData.symbol) || this.tokensList[0]
      this.SET_ACTIVE_TOKEN(this.selectedToken)
    },
    formatAddressPart(addressPart) {
      const addressData = addressPart?.data ?? {}

      this.addressPart = {
        ...addressPart,
        formattedData: {
          coinKey: this.coinData.key,
          coinData: this.coinData,
          coinFamily: this.coinData.family,
          createdAt: this.reportGeneratedAt,
          walletAddress: this.searchValue,
          totalTransactions: this.toComaSeparate(this.txsAddPlus(addressData.numberOfTxs, 2000000)) || '0',
          transactionsSent: this.toComaSeparate(this.txsAddPlus(addressData.totalTxSent, 1000000)) || '0',
          transactionsReceived: this.toComaSeparate(this.txsAddPlus(addressData.totalTxReceived, 1000000)) || '0',
          balance: this.toComaSeparate(this.formatterAmountValue2({
            amount: addressData.balance,
            decimals: this.coinData.family == 'eth' ? 0 : this.selectedToken.decimals ?? this.coinData.decimals,
            symbol: this.selectedToken?.address == 'all' ? this.coinData.symbol : this.selectedToken.symbol ?? this.coinData.symbol,
          })),
          price: addressData.price,
          priceTimestamp: addressData.priceTimestamp,
          totalSent: this.toComaSeparate(this.formatterAmountValue2({
            amount: addressData.totalSent,
            decimals: this.selectedToken.decimals ?? this.coinData.decimals,
            symbol: this.selectedToken.symbol ?? this.coinData.symbol,
          })),
          totalSentPrice: addressData.totalSentPrice,
          totalSentPriceTimestamp: addressData.totalSentPriceTimestamp,
          totalReceived: this.toComaSeparate(this.formatterAmountValue2({
            amount: addressData.totalReceived,
            decimals: this.selectedToken.decimals ?? this.coinData.decimals,
            symbol: this.selectedToken.symbol ?? this.coinData.symbol,
          })),
          totalReceivedPrice: addressData.totalReceivedPrice,
          totalReceivedPriceTimestamp: addressData.totalReceivedPriceTimestamp,
          firstSeen: addressData.firstSeen ? this.formatDate(addressData.firstSeen * 1000, 'dd.MM.yyyy HH:mm') : '',
          lastSeen: addressData.firstSeen ? this.formatDate(addressData.lastSeen * 1000, 'dd.MM.yyyy HH:mm') : '',
        }
      }
    },
    formatOwnerPart(part) {
      if (!this.addressPart?.data?.owner) return

      const ownerPart = part.data ?? {}
      const ownerData = {
        owner: ownerPart.owner,
        description: ownerPart.description,
        type: ownerPart.type,
        tags: ownerPart.tags,
        meta: ownerPart.meta,
        entityData: ownerPart.entityData,
      }

      this.addressPart.data.owner.ownerData = ownerData
    },
    async changeToken(token) {
      const { query } = this.$route

      this.selectedToken = token

      await this.$router
        .replace({
          name: 'report',
          query: { ...query, token: token.address },
        })

      this.initSearchData({ address: query.address, exampleId: query.exampleId, legacy: query.isLegacy })
    },
    takePurchasedInfo(array) {
      const purchasedInfo = array.reduce((acc, item) => {
          acc[item.reportPartName] = item.purchased
          return acc
      }, {})
      this.SET_PURCHASED_INFO(purchasedInfo)
    },
    takeReloadInfo(array) {
      const tier = array.find(e => e.tierWithMaxPrice) || {}
      const reloadInfo = { ...tier.reloadInfo, reportPartName: tier.reportPartName }
      this.SET_RELOAD_INFO(reloadInfo)
    },
    setBarLabels({ sources = {}, currencies = {}}, activeExposurePart) {

      const groupedSourcesByType = sortingObjects(sources, sourcesSortOrderArray)

      this.$set(this.allDataSource, activeExposurePart, groupedSourcesByType.map(item => {
        const localCurrency = this.getSourceCurrency({ source: item, currencies })
        return  {
          ...item,
          funds: {
            ...item.funds,
            default: Boolean(item.funds.default)
          },
          key: item.funds.type,
          tooltip: `${item.funds.type} ${formatShare(item.share)}`,
          pieValue: item.share,
          value: item.share,
          itemStyle: {color: item.funds.default ? this.findColorByTypeScore(-1) : this.findColorByTypeScore(item.funds.score)},
          formattedAmount: this.selectedToken.address === 'all' ? '' :
          this.toComaSeparate(this.formatterAmountValue2({
              amount: item.amount,
              decimals: localCurrency?.decimals ?? this.coinData.decimals,
              symbol: localCurrency?.token || localCurrency?.currency || this.coinData.coin || this.coinData.symbol,
            }))
        }
        }
      ))
    },
    setBarEntity({ sources = [], currencies = {} }, activeExposurePart) {

      const groupedSourcesByOwner = sortingObjects(sources, sourcesSortOrderArray)

      this.$set(this.allDataSourceByOwner, activeExposurePart, groupedSourcesByOwner.map(item => {
        const localCurrency = this.getSourceCurrency({ source: item, currencies })
        return {
            ...item,
            funds: {
              ...item.funds,
              default: Boolean(item.funds.default)
            },
            key: item.owner,
            tooltip: `${item.owner} ${formatShare(item.share)}`,
            pieValue: item.share,
            value: item.share,
            itemStyle: {color: item.funds.default ? this.findColorByTypeScore(-1) : this.findColorByTypeScore(item.funds.score)},
            formattedAmount: this.selectedToken.address === 'all' ? '' :
            this.toComaSeparate(this.formatterAmountValue2({
                amount: item.amount,
                decimals: localCurrency.decimals ?? this.coinData.decimals,
                symbol: localCurrency?.token || localCurrency?.currency || this.coinData.coin || this.coinData.symbol,
              }))
        }
      }))
    },
    setTxSources({sources = [], currencies = {}}, activeExposurePart) {

      sources = sources.map((source) => {
        const localCurrency = this.getSourceCurrency({ source, currencies });

        return {
          ...source,
          share: sources.length === 1 ? 1 : source.share,
          currencyData: localCurrency,
          formattedAmount:  this.toComaSeparate(this.formatterAmountValue2({
            amount: source.amount,
            decimals: localCurrency?.decimals ?? this.coinData.decimals,
            symbol: localCurrency?.token ?? localCurrency?.currency ?? this.coinData.coin ?? this.coinData.label,
          })),
          depthSortValue: this.formatterDepthSortValue(source.depth)
        }
      })

      this.$set(this.currenciesByDirection, activeExposurePart, currencies)
      this.$set(this.pureSources, activeExposurePart, sources)
    },
    getSourceCurrency({ source = {}, currencies = {}  }) {
       return currencies[source.token ?? source.currency]
          ? {
            ...currencies[source.token ?? source.currency],
            decimals: currencies[source.token ?? source.currency].decimals || 0
          }
          : ((source.token ?? source.currency) && appConfig.VUE_APP_ESSENTIAL_REPORT
              ? {
                name: this.coinData.label,
                currency: this.coinData.label,
                decimals: this.coinData.decimals,
                address: source.token ?? source.currency
              }
              : {}
          );
    },
    formatterDepthSortValue(value) {
      if (typeof value === 'number') {
        return value
      }

      if (value && value.minimum && value.maximum) {
        return value.minimum
      } else {
        return null
      }
    },
    scrollToSection(anchor, timeout = 0) {
      if (anchor) {
        const el = document.getElementById(anchor)
        if (el) {
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth' })
          }, timeout)
        }
      }
    },
    onBuyClick(value) {
      this.stopAllTours(this)
      const currentPath = this.$route.fullPath
      localStorage.setItem('comeBackPath', currentPath)
      this.openProfilePlan(value)
    },
    async onReloadReport() {
      this.stopAllTours(this)
      await this.fetchCurrentPlan()

      const { monthlyQuota, reportsRemaining } = this.getCurrentPlan || {};
      const isValidQuota = monthlyQuota !== undefined && monthlyQuota >= 0;

      if (isValidQuota && reportsRemaining <= 5) {
        this.openModal({
          component: LimitsReachedModal,
          props: { limitInfo: { reportsRemaining, monthlyQuota } }
        });
        if (reportsRemaining <= 0) return;
      }

      this.reloadReportLoading = true
      this.reloadReport({
        address: this.addressPart?.formattedData?.walletAddress,
        coin: this.coinData?.key,
        ...(this.coinData.supportContracts ? { token: this.selectedToken.address } : {}),
      })
        .then(e => {
          if (!e.success) return
          this.fetchCurrentPlan()
          this.initQuery()
        })
        .finally(() => {
          this.reloadReportLoading = false
        })
    },
    watcherForScroll() {
      const container = document.getElementById('main-container')
      if (!container) return

      let lastScrollTop = container.scrollTop
      let ticking = false

      const updateHeader = () => {
        const currentScrollTop = container.scrollTop

        if (currentScrollTop > lastScrollTop) {
          document.documentElement.classList.add('header-hidden')
        } else {
          document.documentElement.classList.remove('header-hidden')
        }

        lastScrollTop = currentScrollTop
        ticking = false
      }
      const updateScroll = () => {
        updateHeader()
      }

      this._onContainerScroll = () => {
        if (!ticking) {
          requestAnimationFrame(updateScroll)
          ticking = true
        }
      }

      container.addEventListener('scroll', this._onContainerScroll)
    },
    startReportIntro() {
      this.startReportOnboardingTours({ vm: this })
    },
    changeToAvailableExposurePart() {
      const availableExposurePart = this.getExposurePartOptions.find(part => !part.disabled)
      if (this.getActiveExposurePart?.value !== availableExposurePart?.value) {
        this.SET_ACTIVE_EXPOSURE_PART(availableExposurePart)
      }
    },
    setFieldInExposurePart(activeExposurePart, field, value) {
      const opIndex = this.getExposurePartOptions.findIndex(({ value }) => value === activeExposurePart)
      if (opIndex !== -1) {
        let options = this.getExposurePartOptions
        options[opIndex][field] = value
        this.SET_EXPOSURE_PART_OPTIONS(options)
      }
    },
    changeToAvailableActivePartBar() {
      const availablePart = this.getPartBarOptions.find(part => !part.disabled)
      this.SET_ACTIVE_PART_BAR({})
      this.$nextTick(()=> {
        this.SET_ACTIVE_PART_BAR(availablePart)
      })
    },
    setFieldInActivePartBart(activePart, field, value) {
      const opIndex = this.getPartBarOptions.findIndex(({ value }) => value === activePart)
      if (opIndex !== -1) {
        let options = this.getPartBarOptions
        options[opIndex][field] = value
        this.SET_PART_BAR_OPTIONS(options)
      }
    },
  },
}
</script>

<style>
:root {
  --sticky-top-buy-block: 0;
}
.page-wrapper:has(.report-wrapper) {
  overflow: unset;
}
.report-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
  padding-top: calc(var(--header-height) + 104px);
  padding-bottom: calc(var(--report-banner-height) + 24px);
  background: var(--Background-S0, #F5F5F7);
}

.report-wrapper--legacy {
  padding-top: calc(var(--report-header-banner-height) + 168px);
}

.app-root:has(.purchase-wrapper) .report-wrapper {
  padding-bottom: 0;
}

.report {
  width: var(--main-content-width);
  max-width: var(--main-content-max-width);
}

.report-back-arrow-wrapper {
  display: inline-flex;
  align-items: center;
  margin-top: 28px;
  margin-bottom: 30px;
  cursor: pointer;
  user-select: none;
}

.report .info-block-key {
  max-width: 200px;
}

.report__header {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--pale-blue);
}

.report-section__content-wrapper {
  display: flex;
  gap: 24px;
}
.report-block-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@media screen and (max-width: 650px) {
  .report-section__content-wrapper {
    flex-direction: column;
  }
}

.report-block__header {
  align-items: center;
  display: flex;
  font-size: 24px;
  padding: 14px;
  font-weight: 600;
  border: 1px solid #fff;
  border-bottom: 0;
  border-radius: 10px 10px 0px 0px;
}

.report-block__header--sof {
  background: rgba(240, 242, 249, 1);
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  margin: 12px 0;
}

.report-type-title {
  color: var(--Texts-General-Primary, #0E0B3A);
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 30px;
  margin-bottom: 16px;
  display: none;
}

.report__loader {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100dvh;
}

.scale-appear-enter-active {
  transition: all .3s ease-in;
}

.scale-appear-leave-active {
  transition: all .3s ease-out;
}

.scale-appear-enter,
.scale-appear-leave-to {
  transform: scale(0.95);
  opacity: 0;
}

@media (max-width: 767px) {
  .report {
    width: 100%;
  }
  .report-back-arrow-wrapper {
    display: none;
  }
}
</style>

<style scoped>
.report-wrapper::v-deep .gl-skeleton-amount__wrapper {
  display: none;
}
.report-wrapper::v-deep .report__risk-block-wrapper--skeleton .gl-skeleton-amount__wrapper,
.report-wrapper::v-deep .report__tier2-block-wrapper--skeleton .gl-skeleton-amount__wrapper,
.report-wrapper::v-deep .report__full-block-wrapper--skeleton .gl-skeleton-amount__wrapper {
  display: inline-flex;
}
</style>
